'use client';

import React, { useState, useRef, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import { useActiveHeading } from '@/hooks/use-active-heading';
import type { Heading } from '@/types/blog';

interface TableOfContentsProps {
  headings: Heading[];
}

// Constants
const SCROLL_OFFSET_PX = 96; // Account for sticky nav (top-24 = 6rem = 96px) when scrolling to heading
const HIGHLIGHT_DURATION_MS = 2000; // How long to highlight clicked item
const FOCUS_SCROLL_PADDING_PX = 16; // Padding when scrolling focused item into view
const INDENT_PER_LEVEL_REM = 1.25; // Indentation per heading level

/**
 * Table of Contents component with smooth scrolling and active heading detection.
 *
 * Features:
 * - IntersectionObserver for active heading detection
 * - Throttled scroll handler for performance
 * - Smooth scroll navigation on click
 * - Focus-aware scrolling in nav container
 * - Prevents jumpy scrolling during user interaction
 *
 * @param headings - Array of heading data (id, text, level)
 */
export function TableOfContents({ headings }: TableOfContentsProps) {
  const [highlightedId, setHighlightedId] = useState<string>('');
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  const isUserInteractingRef = useRef<boolean>(false);
  const userInteractionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActiveIdRef = useRef<string>('');

  // Use custom hook for active heading detection
  // headerOffset: 96px matches sticky top-24 (6rem = 96px)
  const activeId = useActiveHeading(headings, {
    headerOffset: 96,
  });

  // Cleanup timeouts on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
      if (userInteractionTimeoutRef.current) {
        clearTimeout(userInteractionTimeoutRef.current);
        userInteractionTimeoutRef.current = null;
      }
    };
  }, []);

  // Auto-scroll TOC to show active item, but only if user is not interacting
  // Debounced to prevent jumpy scrolling when active heading changes frequently
  useEffect(() => {
    // Skip if no active ID or same as last, or user is interacting
    if (!activeId || activeId === lastActiveIdRef.current || isUserInteractingRef.current) {
      lastActiveIdRef.current = activeId;
      return;
    }

    lastActiveIdRef.current = activeId;

    // Debounce auto-scroll to prevent jumpy behavior
    // Increased debounce and made threshold more aggressive to reduce jumpiness
    const timeoutId = setTimeout(() => {
      // Only auto-scroll if user is not interacting
      if (isUserInteractingRef.current) return;

      // Find the active link element
      const activeLink = navRef.current?.querySelector(`a[href="#${activeId}"]`) as HTMLElement;
      if (!activeLink || !navRef.current) return;

      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        // Only auto-scroll if user is not interacting
        if (isUserInteractingRef.current) return;

        // Check refs are still valid (could be null if component unmounted)
        if (!navRef.current || !activeLink) return;

        const itemRect = activeLink.getBoundingClientRect();
        const navRect = navRef.current.getBoundingClientRect();
        const navScrollTop = navRef.current.scrollTop;
        const navHeight = navRect.height;

        // Only scroll if item is significantly out of view (at least 30% of nav height)
        // This prevents micro-scrolling that causes jumpiness
        const threshold = navHeight * 0.3;
        const isSignificantlyAbove = itemRect.top < navRect.top - threshold;
        const isSignificantlyBelow = itemRect.bottom > navRect.bottom + threshold;

        if (isSignificantlyAbove) {
          navRef.current.scrollTo({
            top: navScrollTop + (itemRect.top - navRect.top) - FOCUS_SCROLL_PADDING_PX,
            behavior: 'smooth',
          });
        } else if (isSignificantlyBelow) {
          navRef.current.scrollTo({
            top: navScrollTop + (itemRect.bottom - navRect.bottom) + FOCUS_SCROLL_PADDING_PX,
            behavior: 'smooth',
          });
        }
      });
    }, 300); // Increased debounce to 300ms to prevent rapid scrolling

    return () => {
      clearTimeout(timeoutId);
    };
  }, [activeId]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, headingId: string) => {
    e.preventDefault();

    // Mark user as interacting to prevent auto-scroll
    isUserInteractingRef.current = true;
    if (userInteractionTimeoutRef.current) {
      clearTimeout(userInteractionTimeoutRef.current);
    }
    // Reset interaction flag after a delay to allow auto-scroll again
    userInteractionTimeoutRef.current = setTimeout(() => {
      isUserInteractingRef.current = false;
      userInteractionTimeoutRef.current = null;
    }, 1000);

    const element = document.getElementById(headingId);
    if (element) {
      // Highlight the clicked item temporarily
      setHighlightedId(headingId);

      // Clear any existing timeout
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }

      // Remove highlight after duration
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedId('');
        highlightTimeoutRef.current = null;
      }, HIGHLIGHT_DURATION_MS);

      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - SCROLL_OFFSET_PX;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      // Update URL without triggering scroll
      window.history.pushState(null, '', `#${headingId}`);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLAnchorElement>) => {
    // Mark user as interacting to prevent auto-scroll
    isUserInteractingRef.current = true;
    if (userInteractionTimeoutRef.current) {
      clearTimeout(userInteractionTimeoutRef.current);
    }
    // Reset interaction flag after a delay to allow auto-scroll again
    userInteractionTimeoutRef.current = setTimeout(() => {
      isUserInteractingRef.current = false;
      userInteractionTimeoutRef.current = null;
    }, 1000);

    // Ensure focused item is visible without causing jumpy scrolling
    const nav = e.currentTarget.closest('nav');
    if (nav) {
      const itemRect = e.currentTarget.getBoundingClientRect();
      const navRect = nav.getBoundingClientRect();
      const navScrollTop = nav.scrollTop;

      // Only scroll if item is not fully visible
      if (itemRect.top < navRect.top) {
        nav.scrollTo({
          top: navScrollTop + (itemRect.top - navRect.top) - FOCUS_SCROLL_PADDING_PX,
          behavior: 'smooth',
        });
      } else if (itemRect.bottom > navRect.bottom) {
        nav.scrollTo({
          top: navScrollTop + (itemRect.bottom - navRect.bottom) + FOCUS_SCROLL_PADDING_PX,
          behavior: 'smooth',
        });
      }
    }
  };

  if (headings.length === 0) return null;

  return (
    <nav
      ref={navRef}
      className="hidden lg:block w-full max-h-[calc(100vh-8rem)] overflow-y-auto overscroll-contain scroll-smooth"
      aria-label="Table of contents"
    >
      <div className="bg-card/60 backdrop-blur-md rounded-2xl p-4 border shadow-xl shadow-black/20">
        <h3 className="text-xs font-bold mb-3 uppercase tracking-wider text-muted-foreground">
          Table of Contents
        </h3>
        <ul className="space-y-0.5" role="list">
          {headings.map((heading) => {
            const isActive = activeId === heading.id;
            const isHighlighted = highlightedId === heading.id;
            const isH2 = heading.level === 2;
            const indentLevel = heading.level - 2;

            return (
              <li
                key={heading.id}
                className={`transition-all duration-300 ease-in-out ${isActive ? 'opacity-100' : 'opacity-90'}`}
                style={{
                  paddingLeft: `${indentLevel * INDENT_PER_LEVEL_REM}rem`,
                }}
              >
                <a
                  href={`#${heading.id}`}
                  className={`group relative flex items-start gap-2 text-sm transition-all duration-300 ease-in-out rounded-lg px-2.5 py-1.5 -mx-2.5 ${
                    isActive
                      ? 'text-blue-400 font-semibold bg-blue-500/15 border-l-2 border-blue-400 shadow-sm shadow-blue-500/10'
                      : isHighlighted
                        ? 'text-blue-300 font-medium bg-blue-500/10 border-l-2 border-blue-400/50'
                        : isH2
                          ? 'hover:bg-accent font-medium'
                          : 'text-muted-foreground hover:bg-accent/50'
                  }`}
                  onClick={(e) => handleClick(e, heading.id)}
                  onFocus={handleFocus}
                  aria-current={isActive ? 'location' : undefined}
                >
                  {/* Active indicator with smooth animation */}
                  {isActive && (
                    <ChevronRight
                      className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-blue-400 transition-all duration-300 ease-in-out"
                      aria-hidden="true"
                    />
                  )}
                  {!isActive && isH2 && (
                    <span className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 flex items-center justify-center">
                      <span className="h-1.5 w-1.5 rounded-full bg-border group-hover:bg-primary transition-colors duration-300" />
                    </span>
                  )}
                  {!isActive && !isH2 && <span className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />}
                  <span
                    className={`line-clamp-2 leading-relaxed transition-all duration-300 ease-in-out ${
                      isActive ? 'font-semibold' : isH2 ? 'font-medium' : 'font-normal'
                    }`}
                  >
                    {heading.text}
                  </span>
                  {/* Subtle glow effect for active item */}
                  {isActive && (
                    <span
                      className="absolute inset-0 rounded-lg bg-blue-500/5 -z-10 blur-sm transition-opacity duration-300"
                      aria-hidden="true"
                    />
                  )}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
